import { errorToast, warningToast } from '@/helpers';
import { _isNil, _isNotEmpty, _notNil } from '@/littledash';
import InVivoError from '@/model/InVivoError';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import DeviceEventBus, { type DeviceNewReadingDetail } from '@/utils/devices/DeviceEventBus';
import Fuse from 'fuse.js';
import type {
  DataTableRow,
  DataTableService,
  DataTableStateChangeEvent,
  DataTableWorkflowEvent,
} from '../DataTable.model';
import { DataTableEvent } from '../DataTable.model';

/**
 * Subscribes to DeviceEventBus and handles 'focus-search' device readings by
 * finding the matching animal row and snapping the selection to it. When a
 * workflow is active, only consumes the reading if the search step is current.
 */
export class DataTableDeviceFocusSearch {
  readonly #abortController = new AbortController();
  readonly #dataTableService: DataTableService;
  #searchStepActive = false;
  #fuse: Fuse<DataTableRow> | undefined;

  constructor(dataTableService: DataTableService) {
    this.#dataTableService = dataTableService;

    const signal = this.#abortController.signal;

    dataTableService.subscribe(DataTableEvent.WorkflowActionEnter, this.#handleWorkflowEnter, signal);
    dataTableService.subscribe(DataTableEvent.WorkflowActionExit, this.#handleWorkflowExit, signal);
    dataTableService.subscribe(DataTableEvent.TableStateChange, this.#handleTableStateChange, signal);

    DeviceEventBus.subscribe('device:new-reading', this.#handleReading, { signal });
  }

  destroy(): void {
    this.#abortController.abort();
  }

  readonly #handleWorkflowEnter = (event: DataTableWorkflowEvent): void => {
    if (event.detail.source === 'search') {
      this.#searchStepActive = true;
    }
  };

  readonly #handleWorkflowExit = (event: DataTableWorkflowEvent): void => {
    if (event.detail.source === 'search') {
      this.#searchStepActive = false;
    }
  };

  readonly #handleTableStateChange = (event: DataTableStateChangeEvent): void => {
    if (event.detail.type.has('rows')) {
      this.#fuse = undefined;
    }
  };

  #getFuse(): Fuse<DataTableRow> | undefined {
    if (_isNil(this.#fuse)) {
      const rows = this.#dataTableService.rows;
      if (_isNotEmpty(rows)) {
        this.#fuse = new Fuse(rows, {
          includeScore: true,
          threshold: 0,
          ignoreLocation: true,
          ignoreFieldNorm: true,
          keys: [
            ['animal', 'name'],
            ['animal', 'alt_ids', 'donor'],
            ['animal', 'alt_ids', 'ear'],
            ['animal', 'alt_ids', 'tag'],
            ['animal', 'alt_ids', 'tail'],
          ],
        });
      }
    }
    return this.#fuse;
  }

  readonly #handleReading = (event: CustomEvent<DeviceNewReadingDetail>): void => {
    if (document.hidden) {
      return;
    }

    if (event.detail.targetId !== 'focus-search') {
      return;
    }

    const workflowActive = this.#dataTableService.workflowService.workflowActive;

    if (workflowActive && !this.#searchStepActive) {
      return;
    }

    const trimmed = event.detail.value.trim();
    if (trimmed.length === 0) {
      return;
    }

    event.preventDefault();
    this.#processFocusSearch(trimmed, workflowActive);
  };

  #processFocusSearch(value: string, workflowActive: boolean): void {
    try {
      const fuse = this.#getFuse();
      if (_isNil(fuse)) {
        warningToast('Table data is not yet available. Please wait for the table to load.');
        return;
      }

      const results = fuse.search(value);
      const perfectMatches = results.filter((r) => (r.score ?? 1) < 1e-10);

      if (perfectMatches.length === 0) {
        warningToast(`No animal found matching "${value}"`);
        return;
      }

      if (perfectMatches.length > 1) {
        errorToast(`Multiple animals match "${value}"`);
        return;
      }

      const matchedRow = perfectMatches[0].item;
      const rowIndex = this.#dataTableService.toIndexRow(matchedRow.id);
      if (_isNil(rowIndex)) {
        warningToast(`No animal found matching "${value}"`);
        return;
      }

      if (workflowActive) {
        this.#dataTableService.workflowService.workflowActionExit('search', { targetRowIndex: rowIndex });
      } else {
        const currentColumnIndex = this.#dataTableService.selection?.topLeft.column ?? 0;
        const currentColumn = this.#dataTableService.columnByIndex(currentColumnIndex);
        this.#dataTableService.selectCell(
          { column: currentColumnIndex, row: rowIndex },
          { scrollTo: true, activate: false, source: 'api' }
        );
        if (_notNil(currentColumn)) {
          this.#dataTableService.flashCell({ column: currentColumn.id, row: matchedRow.id }, 'blue');
        }
      }
    } catch (err) {
      ExceptionHandler.captureException(
        new InVivoError('Failed to process device focus search', {
          cause: err,
          slug: 'datatable-device-focus-search-error',
          context: { value },
        })
      );
      errorToast('Failed to search for animal from device reading. Please try again.');
    }
  }
}
